import React, { useEffect, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ProductCompositionOutletType } from '../index'
import lookup from '../../../../assets/equipment/Tables/lookup.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import pencil from '../../../../assets/equipment/Tables/pencil.svg'

const TableWrapper = styled.div`
  overflow-y: auto;
  overflow-x: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

const CellContainer = styled.div`
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  width: 100%;
`;

const Tooltip = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  max-width: 360px;
  white-space: pre-wrap;
  word-wrap: break-word;
  display: ${props => (props.visible ? 'block' : 'none')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 10px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #333;
  }
`;
import ViewProductCompositionModal from '../Visualmodalproduct'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const API_BASE_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface Grade {
  id: number
  name: string
  typeId: number
  GraduationType?: {
    id: number
    name: string
  }
}

interface StockColor {
  id: number
  stockId: number
  colorId: number
  Colors: {
    id: number
    name: string
    tenantId: number | null
  }
}

interface Stock {
  id: number
  description: string
  costPerKg?: number
  costPerMm?: number
  StockColors?: StockColor[]
}

interface Material {
  id: number
  name: string
  density: number
  tenantId: number
  Stock?: Stock[]
}

interface Resource {
  id: number
  name: string
  description: string
  categoryId: number
}

interface ScriptMaterial {
  id: number
  scriptId: number
  materialId: number
  unitOfMeasurementId?: number
  UnitOfMeasurement?: { id: number; name: string; abbreviation: string }
  Material: Material
}

interface ScriptResource {
  id: number
  scriptId: number
  resourceId: number
  unitOfMeasurementId?: number
  UnitOfMeasurement?: { id: number; name: string; abbreviation: string }
  Resource: Resource
}

interface Script {
  id: number
  name: string
  productId: number
  ScriptMaterial: ScriptMaterial[]
  ScriptResource: ScriptResource[]
}

interface GradeInProduct {
  id: number
  productId: number
  gradeId: number
  Grade: Grade
}

interface Group {
  id: number
  name: string
  tenantId: number
}

interface Family {
  id: number
  name: string
  collectionId: number
  tenantId: number
}

interface ProductResponse {
  id: number
  norm: string
  name: string
  productCode: string
  collectionId: number
  familyId: number
  groupId: number
  createdAt?: string
  updatedAt?: string
  Group?: Group
  Family?: Family
  Collection?: {
    id: number
    name: string
    tenantId: number
  }
  GradeInProduct?: GradeInProduct[]
  Script?: Script[]
}

interface ProductListItem {
  id: number
  code: string
  name: string
  family: string
  grades: string
  groups: string
  colors: string
}

const CompositionTable: React.FC = () => {
  const { refresh, search, Styled, setIsLoading } =
    useOutletContext<ProductCompositionOutletType>()
  const {
    GlobalTable,
    HeaderLine,
    Th,
    Tr,
    Td,
    TdButtons,
    DivInTd,
    InfoMessage,
  } = Styled
  const navigate = useNavigate()

  const [products, setProducts] = useState<ProductListItem[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(10)
  const [totalProducts, setTotalProducts] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  )
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [tooltipContent, setTooltipContent] = useState('')
  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const handleCellMouseEnter = (
    e: React.MouseEvent,
    content: string,
    key: string,
    id: number,
  ) => {
    const ref = cellRefs.current[`${key}-${id}`]
    if (ref && content) {
      const isOverflowing = ref.scrollWidth > ref.clientWidth
      const items = content.split(',').map((s) => s.trim()).filter(Boolean)
      if (isOverflowing || items.length > 1) {
        setTooltipContent(items.length > 1 ? items.join('\n') : content)
        setTooltipPosition({ x: e.clientX + 10, y: e.clientY - 10 })
        setShowTooltip(true)
      }
    }
  }

  const handleCellMouseLeave = () => {
    setShowTooltip(false)
  }

  const getFirstWithEllipsis = (content: string) => {
    if (!content) return '-'
    const parts = content
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return '-'
    if (parts.length === 1) return parts[0]
    return `${parts[0]}...`
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/paginated`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: 0,
          size: visibleCount,
          search: search || undefined,
        },
      })

      const data = response.data
      let productsData: ProductResponse[] = []
      if (Array.isArray(data)) {
        productsData = data
        setTotalProducts(undefined)
      } else if (data && Array.isArray(data.content)) {
        productsData = data.content
        setTotalProducts(typeof data.totalElements === 'number' ? data.totalElements : undefined)
      }

      let transformedProducts: ProductListItem[] = productsData.map(
        (product) => {

          const family = product.Family?.name || '-';
          

          const grades = product.GradeInProduct?.map(gp => gp.Grade?.name).join(', ') || '-';
          

          const groups = product.Group?.name || '-';
          


          const colorSet = new Set<string>()
          const pics = (product as any).ProductItemColor || []
          for (const pic of pics) {
            const cc = pic.CollectionColor || pic.collectionColor
            if (cc && cc.name) colorSet.add(cc.name)
          }
          const colors = colorSet.size > 0 ? Array.from(colorSet).join(', ') : '-'

          return {
            id: product.id,
            code: product.productCode || product.norm || '-',
            name: product.name || '-',
            family,
            grades,
            groups,
            colors,
          }
        },
      )


      if (search) {
        transformedProducts = transformedProducts.filter(
          (item) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.code.toLowerCase().includes(search.toLowerCase()) ||
            item.family.toLowerCase().includes(search.toLowerCase()),
        )
      }

      setProducts(transformedProducts)
    } catch (err: unknown) {
      console.error('Erro ao buscar produtos:', err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError('Não autorizado. Por favor, faça login novamente.')
          navigate('/login')
        } else if (err.response?.status === 404) {
          setProducts([])
        } else {
          setError('Não foi possível carregar os produtos. Tente novamente.')
        }
      } else {
        setError('Erro inesperado ao carregar produtos.')
      }
    } finally {
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }, [search, refresh, navigate, visibleCount])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleViewDetails = (productId: number) => {
    setSelectedProductId(productId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProductId(null)
  }

  const handleEdit = (productId: number) => {
    navigate(`/panel/product-composition/edit/${productId}`)
  }

  const handleDelete = async (productId: number) => {
    const product = products.find((p) => p.id === productId)
    const productName = product ? product.name : productId

    if (
      window.confirm(
        `Tem certeza que deseja excluir o produto "${productName}"?`,
      )
    ) {
      const token = localStorage.getItem('token')
      if (!token) {
        alert('Sessão expirada. Por favor, faça login novamente.')
        navigate('/login')
        return
      }

      try {
        setLoading(true)
        setError(null)
        await axios.delete(`${API_BASE_URL}/${productId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        setProducts((prevProducts) =>
          prevProducts.filter((product) => product.id !== productId),
        )

        alert('Produto excluído com sucesso!')
      } catch (err: unknown) {
        console.error(`Erro ao excluir produto ${productId}:`, err)
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            setError(
              'Não autorizado para excluir. Por favor, faça login novamente.',
            )
            navigate('/login')
          } else {
            const errorMessage =
              err.response?.data?.message || 'Falha ao excluir o produto.'
            setError(errorMessage)
            alert(errorMessage)
          }
        } else {
          setError('Erro inesperado ao excluir produto.')
          alert('Erro inesperado ao excluir produto.')
        }
      } finally {
        setLoading(false)
      }
    }
  }

  if (loading) {
    return <InfoMessage>Carregando produtos...</InfoMessage>
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  if (products.length === 0) {
    return (
      <InfoMessage>
        {search
          ? 'Nenhum produto encontrado com os critérios de busca.'
          : 'Nenhum produto cadastrado. Clique em "Cadastrar produto" para começar.'}
      </InfoMessage>
    )
  }

  return (
    <>
      <TableWrapper>
        <GlobalTable>
        <thead>
          <HeaderLine>
            <Th>Código</Th>
            <Th>Produto</Th>
            <Th>Família</Th>
            <Th>Grades</Th>
            <Th>Grupos</Th>
            <Th>Cores</Th>
            <Th></Th>
          </HeaderLine>
        </thead>
        <tbody>
          {products.map((product) => (
            <Tr key={product.id}>
              <Td data-label='Código'>{product.code}</Td>
              <Td data-label='Produto'>{product.name}</Td>
              <Td data-label='Família'>
                <CellContainer
                  ref={(el) =>
                    (cellRefs.current[`family-${product.id}`] = el)
                  }
                  onMouseEnter={(e: any) =>
                    handleCellMouseEnter(
                      e,
                      product.family,
                      'family',
                      product.id,
                    )
                  }
                  onMouseLeave={handleCellMouseLeave}
                >
                  {getFirstWithEllipsis(product.family)}
                </CellContainer>
              </Td>
              <Td data-label='Grades'>
                <CellContainer
                  ref={(el) =>
                    (cellRefs.current[`grades-${product.id}`] = el)
                  }
                  onMouseEnter={(e: any) =>
                    handleCellMouseEnter(e, product.grades, 'grades', product.id)
                  }
                  onMouseLeave={handleCellMouseLeave}
                >
                  {getFirstWithEllipsis(product.grades)}
                </CellContainer>
              </Td>
              <Td data-label='Grupos'>
                <CellContainer
                  ref={(el) =>
                    (cellRefs.current[`groups-${product.id}`] = el)
                  }
                  onMouseEnter={(e: any) =>
                    handleCellMouseEnter(e, product.groups, 'groups', product.id)
                  }
                  onMouseLeave={handleCellMouseLeave}
                >
                  {getFirstWithEllipsis(product.groups)}
                </CellContainer>
              </Td>
              <Td data-label='Cores'>
                <CellContainer
                  ref={(el) =>
                    (cellRefs.current[`colors-${product.id}`] = el)
                  }
                  onMouseEnter={(e: any) =>
                    handleCellMouseEnter(e, product.colors, 'colors', product.id)
                  }
                  onMouseLeave={handleCellMouseLeave}
                >
                  {getFirstWithEllipsis(product.colors)}
                </CellContainer>
              </Td>
              <TdButtons>
                <DivInTd className='hover-blue' onClick={() => handleViewDetails(product.id)}>
                  <img src={lookup} alt='Visualizar' title='Visualizar' />
                </DivInTd>
                <DivInTd className='hover-blue' onClick={() => handleEdit(product.id)}>
                  <img src={pencil} alt='Editar' title='Editar' />
                </DivInTd>
                <DivInTd className='hover-red' onClick={() => handleDelete(product.id)}>
                  <img src={trash} alt='Excluir' title='Excluir' />
                </DivInTd>
              </TdButtons>
            </Tr>
          ))}
        </tbody>
      </GlobalTable>
      <LoadMoreButton
        currentCount={products.length}
        totalCount={totalProducts}
        hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
        onLoadMore={() => setVisibleCount((prev) => prev + 10)}
      />
      </TableWrapper>

      <Tooltip
        visible={showTooltip}
        x={tooltipPosition.x}
        y={tooltipPosition.y}
      >
        {tooltipContent}
      </Tooltip>

      {isModalOpen && selectedProductId && (
        <ViewProductCompositionModal
          productId={selectedProductId}
          onClose={handleCloseModal}
        />
      )}
    </>
  )
}

export default CompositionTable